import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../server/api';
import { IGetMedals, IMedal } from '../../interfaces/IMedals';
import avatar from '../../assets/images/avatar.png';

// Cache medals-settings per clientUrl to avoid duplicate API calls
const medalsSettingsCache = new Map<string, IGetMedals>();

interface PlayerAvatarProps {
  photoURL?: string;
  fallback?: string;
  size?: number;
  className?: string;
  style?: React.CSSProperties;
  imgClassName?: string;
  imgStyle?: React.CSSProperties;
  /** Pass a specific player id to show that player's medal instead of the current user's */
  playerId?: string;
  /** Override the clientUrl used for medal API calls (useful on public pages where useAuth has no client) */
  clientUrl?: string;
}

export function PlayerAvatar({
  photoURL,
  fallback = avatar,
  size = 100,
  className,
  style,
  imgClassName,
  imgStyle,
  playerId,
  clientUrl: clientUrlProp,
}: PlayerAvatarProps) {
  const { client, player, signed } = useAuth();
  const [medals, setMedals] = useState<IGetMedals | undefined>(undefined);
  const [playerMedal, setPlayerMedal] = useState<IMedal | undefined>(undefined);

  const resolvedClientUrl = clientUrlProp ?? client?.clientUrl;
  const targetPlayerId = playerId ?? player?.id;

  useEffect(() => {
    if (!resolvedClientUrl || !signed) return;

    const cached = medalsSettingsCache.get(resolvedClientUrl);
    if (cached) {
      setMedals(cached);
    } else {
      api
        .get(`/clients/${resolvedClientUrl}/medals-settings`)
        .then(({ data }) => {
          if (data.body) {
            medalsSettingsCache.set(resolvedClientUrl, data.body);
            setMedals(data.body);
          }
        })
        .catch(() => {});
    }

    if (!targetPlayerId) return;
    api
      .get(`/medals/${resolvedClientUrl}/${targetPlayerId}`)
      .then(({ data }) => {
        if (data.body) setPlayerMedal(data.body);
      })
      .catch(() => {});
  }, [resolvedClientUrl, targetPlayerId]);

  // For the current player, also check regional status.
  // For other players (playerId provided), just rely on the API response.
  const showMedal =
    medals?.enabled &&
    playerMedal?.imageUrl &&
    (playerId ? true : player?.regional === 'sim');

  const medalSize = Math.max(Math.round(size * 0.28), 16);

  return (
    <div
      className={className}
      style={{
        position: 'relative',
        display: 'inline-flex',
        width: size,
        height: size,
        flexShrink: 0,
        ...style,
      }}
    >
      <img
        src={photoURL || fallback}
        alt=""
        className={imgClassName}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          borderRadius: '50%',
          ...imgStyle,
        }}
      />
      {showMedal && (
        <img
          src={playerMedal!.imageUrl}
          alt=""
          style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: medalSize,
            height: medalSize,
            borderRadius: '50%',
            border: '1px solid var(--border-color)',
          }}
        />
      )}
    </div>
  );
}
